import { CellWithAddons } from '@elliemae/ds-datagrids';
import { safeSum } from '@frontend/utils';
import {
  AttributionCategory,
  generatePricingGridRows,
  LockRatesAdjustment,
  mapAdjustments,
  OriginLoanRateLockAdjustment,
} from '@frontend/pricing-search';
import Common from '../../../../common/services/Common';
import Rights from '../../../../common/services/Rights';
import { formatPercentValue } from '../../../../common/utils/shared';

const customStyles = {
  textAlign: 'right',
  maxHeight: '36px',
  height: '100%',
  width: 'calc(100% + 1.23076rem)',
  marginLeft: '-0.61538rem',
  marginRight: '-0.61538rem',
  paddingLeft: '0.61538rem',
  paddingRight: '0.61538rem',
  background: '#EBF6FF',
  fontWeight: 700,
  fontSize: 14,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  borderTop: 'solid 1px #1394E5',
};

const DivTextUpper = (name) => (
  <div
    style={{
      fontWeight: 700,
      fontSize: 13,
    }}
  >
    {name}
  </div>
);

const DivTextRight = (data) => (
  <div style={{ width: '100%', textAlign: 'right' }}>{data}</div>
);

export const adjustmentColumns = [
  {
    property: 'name',
    label: 'Name',
    visible: true,
    width: '70%',
    midWidth: 0,

    customRenderer: ({ metaData: { rowData, rowIndex } }) => {
      if (rowIndex === 0) {
        return (
          <CellWithAddons
            badgeAddon={false}
            value={DivTextUpper(rowData.name)}
          />
        );
      }
      if (rowData.title) {
        return DivTextUpper(rowData.name);
      }
      return <div style={{ paddingLeft: `16px` }}>{rowData.name}</div>;
    },
  },
  {
    property: 'margin',
    label: 'Margin',
    visible: true,
    width: '30%',
    midWidth: 0,
    header: {
      customRenderer: () => DivTextRight('Margin'),
    },
    customRenderer: ({ metaData: { rowData, rowIndex } }) => {
      if (rowIndex === 0) {
        return (
          <div
            style={{
              ...customStyles,
              background: '#fff',
              borderBottom: `.07692rem solid #ebedf0`,
              borderTop: `0`,
            }}
          >
            {rowData.margin}
          </div>
        );
      }
      if (rowData.title) {
        return DivTextUpper(rowData.margin);
      }
      return DivTextRight(rowData.margin);
    },
  },
];

export interface TransformRateSelectorDataToSlideOptions {
  pricingAdjustments?: LockRatesAdjustment[];
  armIndexValue: number;
  loanAdjustments?: any;
  loanAdjustmentDetails?: any[];
  attributionCategories?: AttributionCategory[];
}

/**
 * Transforms rate selector data into slide format with pricing and adjustment rows.
 * Filters adjustments based on user roles and rights, then generates formatted rows
 * for display in the pricing grid.
 * @param {TransformRateSelectorDataToSlideOptions} options - Transform options
 * @param {LockRatesAdjustment[]} options.pricingAdjustments - Array of adjustment objects containing pricing data
 * @param {number} options.armIndexValue - ARM index value to calculate final margin adjustment
 * @param {object} options.loanAdjustments - Loan-level concession and origin adjustments
 * @param {any[]} options.loanAdjustmentDetails - Adjustment details adjustments
 * @returns {object} Object containing pricingRows and adjustmentRows arrays
 */
export const transformRateSelectorDataToSlide = ({
  pricingAdjustments = [],
  armIndexValue,
  loanAdjustments = {},
  loanAdjustmentDetails = [],
  attributionCategories = [],
}: TransformRateSelectorDataToSlideOptions) => {
  const roles = Common.getUserRoles() || [];
  const viewLOCompRole = 20;
  const hasLOCompViewRight =
    Array.isArray(roles) &&
    roles.some(({ roleId }) => Number(roleId) === viewLOCompRole);

  const mappedAdjustments = mapAdjustments({
    AdjustmentDetailsAdjustments: loanAdjustmentDetails || [],
    LockRateAdjustments: pricingAdjustments,
    ConcessionAdjustments: [
      ...(loanAdjustments.BranchPriceConcession ?? []),
      ...(loanAdjustments.CorporatePriceConcession ?? []),
    ],
    OriginAdjustments: [
      ...(loanAdjustments.LockExtensionAdjustment ?? []),
      ...(loanAdjustments.ReLockFeeAdjustment ?? []),
      ...(loanAdjustments.CustomPriceAdjustment ?? []),
      ...(loanAdjustments.CustomRateAdjustment ?? []),
      // Saved generated adjustments from the prior lock duplicate the fresh adjustment details
      ...(loanAdjustmentDetails?.length
        ? []
        : loanAdjustments.Adjustment ?? []),
    ],
    AttributionCategories: attributionCategories,
  });
  const pricingRows = generatePricingGridRows({
    mappedAdjustments,
    rights: Rights,
  });

  let newIdAdjustment = 3;

  const finalPrice = mappedAdjustments.find(
    ({ adjustmentType }) => adjustmentType === 'Final',
  );
  const basePrice = mappedAdjustments.find(
    ({ adjustmentType }) => adjustmentType === 'Base',
  );
  const adjustmentRows = [
    {
      id: 1,
      name: 'Final Margin Adjustment',
      margin: formatPercentValue(safeSum(finalPrice?.margin, armIndexValue)),
    },
    {
      id: 2,
      name: 'Index',
      margin: `${Number(armIndexValue || 0).toFixed(3)}%`,
    },
    {
      id: 3,
      name: 'Margin',
      margin: formatPercentValue(basePrice?.margin),
    },
  ];
  mappedAdjustments.forEach((item) => {
    const { margin, description, adjustmentType } = item;
    if (
      typeof margin === 'number' &&
      !['Base', 'Final', 'AllIn'].includes(adjustmentType)
    ) {
      newIdAdjustment += 1;
      const percentMargin = formatPercentValue(margin);
      const newMarginRow = {
        id: newIdAdjustment,
        name: description,
        margin: percentMargin,
      };
      adjustmentRows.push(newMarginRow);
    }
  });

  return {
    pricingRows,
    adjustmentRows,
  };
};
